// Sample numbers (docs/plans/phase1-lab.md §5.8, P2).
//
// A sample number is an ordinary per-facility human number (human-number.ts,
// facility_sequences) with two extra constraints, because it is printed as a Code 128
// barcode on a 50 × 25 mm tube label and read back by a scanner:
//
// - every character must be printable ASCII (what Code 128 code set B can carry), and
// - the number must stay short enough for the bars to be wide enough to scan.

import type { HumanNumberErrorCode, NumberFormat } from '../human-number.ts';
import { compileNumberFormat } from '../human-number.ts';
import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

/**
 * The longest sample number we print. At 16 characters a Code 128 symbol with quiet zones
 * is about 200 modules; at 0.25 mm per module (two dots of a 203 dpi printer) that is the
 * width of a 50 mm label. Mostly-digit numbers are much shorter, because code set C packs
 * two digits into one symbol.
 */
export const SAMPLE_NUMBER_MAX_LENGTH = 16;

export type SampleNumberFormatErrorCode =
  HumanNumberErrorCode | 'sample_number.not_barcode_safe' | 'sample_number.too_long';

export const SAMPLE_NUMBER_ERRORS: Record<
  'sample_number.not_barcode_safe' | 'sample_number.too_long',
  string
> = {
  'sample_number.not_barcode_safe':
    'A sample number can use plain keyboard letters, digits and punctuation only',
  'sample_number.too_long': `A sample number is at most ${SAMPLE_NUMBER_MAX_LENGTH} characters, so its barcode fits the label`,
};

/** Printable ASCII, space to tilde: what Code 128 code set B encodes. */
export function isBarcodeSafe(text: string): boolean {
  return /^[\x20-\x7e]+$/.test(text);
}

/**
 * The length of a number in this format when its sequence has grown to `sequenceDigits`
 * digits (never less than the padding width).
 */
export function sampleNumberLength(
  format: NumberFormat,
  facilityCodeLength: number,
  sequenceDigits: number,
): number {
  let length = 0;
  for (const part of format.parts) {
    switch (part.kind) {
      case 'literal':
        length += part.text.length;
        break;
      case 'sequence':
        length += Math.max(part.width, sequenceDigits);
        break;
      case 'facility':
        length += facilityCodeLength;
        break;
      case 'year':
        length += part.digits;
        break;
      case 'month':
      case 'day':
        length += 2;
        break;
    }
  }
  return length;
}

/**
 * Checks a sample number template for a facility. The limit is applied with the sequence
 * at its padded width, or at 7 digits if the padding is narrower (ten million samples),
 * so a format doesn't pass today and overflow the label next year.
 */
export function checkSampleNumberFormat(
  template: string,
  facilityCode: string,
): Result<NumberFormat, SampleNumberFormatErrorCode> {
  const compiled = compileNumberFormat(template);
  if (!compiled.ok) return compiled;
  const format = compiled.value;
  for (const part of format.parts) {
    if (part.kind === 'literal' && !isBarcodeSafe(part.text)) {
      return err('sample_number.not_barcode_safe');
    }
  }
  // A facility code is ASCII letters and digits (human-number.ts refuses anything else when
  // rendering), so only its length matters here.
  const usesFacility = format.parts.some((p) => p.kind === 'facility');
  const facilityLength = usesFacility ? facilityCode.length : 0;
  if (sampleNumberLength(format, facilityLength, 7) > SAMPLE_NUMBER_MAX_LENGTH) {
    return err('sample_number.too_long');
  }
  return ok(format);
}
